import React from "react";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { Box, Popover, Stack, Typography } from "@mui/material";
import { MutedPlaceholder } from "components/MutedPlaceholder";

/** One named person on a billing account. */
export type ContactView = { name: string; email: string; roles: string[]; isBilling: boolean };

/**
 * One side of the purchase: the end customer, or the reseller that is billed for them.
 *
 * `unavailable` is set when no source can answer for this party's contacts. It is not the same as an
 * empty `contacts` list, which means the account is readable and holds nobody. The card says which
 * of the two it is and invents nothing either way.
 */
export type ContactPartyView = {
  legalName: string;
  address?: string;
  isIndividual: boolean;
  contacts: ContactView[];
  unavailable?: boolean;
};

const endCustomerHeading = "End customer";
const resellerHeading = "Reseller (billed party)";
const contactsHeading = "Contacts";

/** Said in full, because a reader who is told nothing assumes the account has no contacts. */
const unavailableMessage = "Contact details are not available yet.";

function ContactRow({ contact }: { contact: ContactView }) {
  return (
    <Box sx={{ mb: 0.75, opacity: contact.isBilling ? 1 : 0.72 }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <BadgeOutlinedIcon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
        <Typography variant="body2">{contact.name}</Typography>
        {contact.isBilling && (
          // Emphasis, not success: the tag marks which of several contacts billing goes to.
          <Typography variant="overline" color="primary" sx={{ lineHeight: 1 }}>
            Billing contact
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 3.5 }}>
        {contact.email}
        {contact.roles.length > 0 && (
          <>
            <br />
            {contact.roles.join(", ")}
          </>
        )}
      </Typography>
    </Box>
  );
}

function PartyColumn({ heading, party }: { heading: string; party: ContactPartyView }) {
  const PartyIcon = party.isIndividual ? PersonOutlinedIcon : BusinessOutlinedIcon;

  return (
    <Box sx={{ minWidth: 220 }}>
      <Typography variant="overline" color="text.secondary" component="div">
        {heading}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <PartyIcon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {party.legalName}
        </Typography>
      </Stack>
      {party.address && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 3.5, mb: 1 }}>
          {party.address}
        </Typography>
      )}
      <Typography variant="overline" color="text.secondary" component="div">
        {contactsHeading}
      </Typography>
      {party.unavailable ? (
        <Typography variant="body2" color="text.secondary">
          {unavailableMessage}
        </Typography>
      ) : party.contacts.length === 0 ? (
        <MutedPlaceholder>None</MutedPlaceholder>
      ) : (
        party.contacts.map((contact) => <ContactRow key={contact.email} contact={contact} />)
      )}
    </Box>
  );
}

/**
 * Raises the purchasing parties behind whatever the caller wraps - a `Billing Account` link on a
 * list row, or the account name in the builder. Read-only, so the card takes no pointer events and
 * the hover stays with the trigger.
 * @param root0
 * @param root0.children
 * @param root0.endCustomer
 * @param root0.reseller
 */
export function ContactsHoverCard({
  children,
  endCustomer,
  reseller,
}: {
  children: React.ReactElement;
  endCustomer: ContactPartyView;
  reseller?: ContactPartyView;
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const trigger = React.cloneElement(children, {
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
    onMouseLeave: () => setAnchorEl(null),
  });

  return (
    <>
      {trigger}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
      >
        <Stack
          direction="row"
          spacing={2}
          divider={<Box sx={{ borderLeft: 1, borderColor: "divider" }} />}
          sx={{ p: 1.5 }}
        >
          <PartyColumn heading={endCustomerHeading} party={endCustomer} />
          {reseller && <PartyColumn heading={resellerHeading} party={reseller} />}
        </Stack>
      </Popover>
    </>
  );
}
